// Case Buddy embedded Tool 88 hop-solver integration methods.
// These methods intentionally keep Main Data as spreadsheet-shaped truth.
// Tool 88 output is staged/reviewed first, then handed to existing Raw Data or path-operation flows.

import {
  buildRawDataAppendMatrix,
  buildTsv,
  CASE_DEFAULT_COLS,
  CASE_DEFAULT_ROWS,
  normalizeCaseMatrix,
  normalizeCell,
  parseCaseEntries,
  rowHasMeaningfulData
} from '../CaseBuddyCore.js';

function caseHopSolverNormalizeKey(value = '') {
  return normalizeCell(value).toLowerCase();
}

function caseHopSolverNowUtc() {
  return new Date().toISOString();
}

function caseHopSolverCloneRow(row = []) {
  const source = Array.isArray(row) ? row.slice(0, CASE_DEFAULT_COLS) : [];
  return source.length >= CASE_DEFAULT_COLS ? source : [...source, ...Array(CASE_DEFAULT_COLS - source.length).fill('')];
}

function caseHopSolverShortHash(value = '') {
  const text = normalizeCell(value);
  if (!text) return '';
  if (text.length <= 16) return text;
  return `${text.slice(0, 8)}...${text.slice(-6)}`;
}

function caseHopSolverNormalizeToken(value = '') {
  const text = normalizeCell(value);
  return /^(?:null|undefined|nan)$/i.test(text) ? '' : text;
}

function caseHopSolverNormalizeHeaderText(value = '') {
  return normalizeCell(value).replace(/[\s_./-]+/g, '').toLowerCase();
}

function caseHopSolverRowLooksLikeRawHeader(row = []) {
  if (!Array.isArray(row)) return false;
  const cells = row.slice(0, 22).map(caseHopSolverNormalizeHeaderText);
  const hashHeader = ['transactionhash', 'txhash', 'hash'].includes(cells[1]);
  const toHeader = ['toaddress', 'to', 'destinationaddress', 'recipientaddress'].includes(cells[3]);
  const amountHeader = ['amount', 'amount1', 'value'].includes(cells[5]) || ['amount', 'amount1', 'value'].includes(cells[7]);
  const tokenHeader = ['tokensymbol', 'token', 'asset', 'assettype'].includes(cells[6]);
  const labelHeader = ['pathlabel', 'label'].includes(cells[0]);
  const continuesHeader = ['continuesas', 'continues', 'pathends'].includes(cells[16]);
  return hashHeader && (toHeader || amountHeader || tokenHeader || labelHeader || continuesHeader);
}

function caseHopSolverFormatAmountToken(amount = '', token = '') {
  const amountText = normalizeCell(amount);
  const tokenText = caseHopSolverNormalizeToken(token);
  if (!amountText) return '';
  return tokenText && !new RegExp(`\\b${tokenText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(amountText)
    ? `${amountText} ${tokenText}`
    : amountText;
}


function createDefaultCaseAutomationTool88Activity() {
  return {
    active: false,
    startedAtUtc: '',
    updatedAtUtc: '',
    completedAtUtc: '',
    status: '',
    title: '',
    message: '',
    detail: '',
    eventName: '',
    integrationAction: '',
    stepCount: 0,
    researchedCount: 0,
    entryCount: 0,
    endingCount: 0,
    completeCount: 0,
    unresolvedCount: 0,
    flowComplete: false,
    flowCompleteConfirmations: 0,
    rawRows: 0,
    rowsAppended: 0,
    targetLabel: '',
    targetHash: '',
    targetAddress: '',
    guardrailMessage: '',
    lastLogKey: '',
    lastAutomationLogKey: '',
    log: []
  };
}

function caseHopSolverActivityToProgress(activity = {}) {
  const source = activity && typeof activity === 'object' ? activity : createDefaultCaseAutomationTool88Activity();
  return {
    active: !!source.active,
    title: normalizeCell(source.title),
    detail: normalizeCell(source.detail || source.message),
    status: normalizeCell(source.status),
    action: normalizeCell(source.integrationAction),
    stepCount: Number(source.stepCount) || 0,
    researchedCount: Number(source.researchedCount) || 0,
    outputRows: Number(source.rawRows) || 0,
    totalEntries: Number(source.entryCount) || 0,
    endingCount: Number(source.endingCount) || 0,
    completeEndings: Number(source.completeCount) || 0,
    openEndings: Number(source.unresolvedCount) || 0,
    flowCompleteConfirmations: Number(source.flowCompleteConfirmations) || 0,
    flowCompleteRequiredConfirmations: 0,
    selectedLabel: normalizeCell(source.targetLabel),
    selectedHash: normalizeCell(source.targetHash),
    selectedTarget: normalizeCell(source.targetAddress),
    lastEventKey: normalizeCell(source.lastLogKey),
    lastLoggedAtMs: 0,
    events: (Array.isArray(source.log) ? source.log : []).map((item, index) => ({
      id: item.id || `${source.updatedAtUtc || source.startedAtUtc || 'tool88'}-${index}`,
      label: normalizeCell(item.message || item.label || item.detail)
    })).filter(item => item.label).slice(0, 6),
    updatedAtUtc: normalizeCell(source.updatedAtUtc || source.completedAtUtc || source.startedAtUtc)
  };
}

function caseHopSolverCompactMessage(value = '', maxLength = 160) {
  const text = normalizeCell(value).replace(/\s+/g, ' ');
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(12, maxLength - 1)).trim()}…`;
}

function caseHopSolverSnapshotMessage(snapshot = {}, runState = {}) {
  return caseHopSolverCompactMessage(
    snapshot.message ||
    runState.detail ||
    snapshot.flowCompletion?.summary ||
    snapshot.guardrail?.message ||
    snapshot.status ||
    'Tool 88 is solving unresolved path endings.'
  );
}

function caseHopSolverSnapshotTitle(snapshot = {}, runState = {}) {
  const status = normalizeCell(snapshot.status || runState.status).replace(/[-_]+/g, ' ');
  const action = normalizeCell(snapshot.integrationAction).replace(/[-_]+/g, ' ');
  if (snapshot.guardrail) return 'Paused for review';
  if (snapshot.flowComplete && Number(runState.flowCompleteConfirmations || 0) > 0) return 'Confirming flow complete';
  if (snapshot.flowComplete) return 'Flow complete';
  if (/trace|through/i.test(action)) return 'Tracing through next hop';
  if (/selected output|hydrate|missing/i.test(status)) return 'Hydrating selected output';
  if (/automation started/i.test(status)) return 'Starting Tool 88';
  if (status) return status;
  return 'Solving raw-data hops';
}

function caseHopSolverSelectedEntryTarget(snapshot = {}) {
  const entry = snapshot?.selectedEntry || {};
  const pathLabel = normalizeCell(entry.pathLabel || entry.label);
  const txHash = caseHopSolverShortHash(entry.txHash || entry.hash || entry.transactionHash);
  const targetAddress = caseHopSolverShortHash(snapshot.selectedTargetAddress || entry.toAddress || entry.targetAddress);
  return {
    targetLabel: pathLabel,
    targetHash: txHash,
    targetAddress
  };
}


function caseHopSolverParseAmount(value = '') {
  const text = normalizeCell(value).replace(/,/g, '');
  const match = text.match(/-?\d+(?:\.\d+)?(?:e[+-]?\d+)?/i);
  if (!match) return null;
  const number = Number(match[0]);
  return Number.isFinite(number) ? number : null;
}

function caseHopSolverRowHasBadNullAmountToken(row = []) {
  return /\b(?:0\s+null|0\s+undefined|0\s+nan|null|undefined|nan)\b/i.test([
    row?.[5],
    row?.[6],
    row?.[9],
    row?.[12],
    row?.[15],
    row?.[16],
    row?.[17],
    row?.[21]
  ].map(value => normalizeCell(value)).join(' '));
}

function caseHopSolverExtractFundsToken(value = '', fallbackToken = '') {
  const fallback = caseHopSolverNormalizeToken(fallbackToken);
  const text = normalizeCell(value).replace(/,/g, ' ');
  if (!text) return fallback;
  const match = text.match(/-?\d+(?:\s+\d{3})*(?:\.\d+)?(?:e[+-]?\d+)?\s+([A-Za-z][A-Za-z0-9._/-]*)/i);
  return caseHopSolverNormalizeToken(match?.[1]) || fallback;
}

function caseHopSolverHashRowQuality(row = []) {
  const hash = normalizeCell(row?.[1]);
  const toAddress = normalizeCell(row?.[3]);
  const amount = caseHopSolverParseAmount(row?.[5]);
  const token = caseHopSolverNormalizeToken(row?.[6]);
  const fundsText = normalizeCell(row?.[12]);
  const fundsAmount = caseHopSolverParseAmount(fundsText);
  const fundsToken = caseHopSolverExtractFundsToken(fundsText, token);
  const continuesAs = normalizeCell(row?.[16]);
  const hasLeftBounds = !!(normalizeCell(row?.[17]) || normalizeCell(row?.[18]));
  const problems = [];

  if (!hash) problems.push('missing hash');
  if (!toAddress) problems.push('missing To address');
  if (caseHopSolverRowHasBadNullAmountToken(row)) problems.push('contains null/undefined/NaN amount text');
  if (!Number.isFinite(amount) || amount <= 0) problems.push('missing amount');
  if (!token) problems.push('missing token');
  if (!Number.isFinite(fundsAmount) || fundsAmount <= 0) problems.push('missing funds-followed amount');
  if (!fundsToken) problems.push('missing funds-followed token');
  if (!continuesAs) problems.push('missing Continues As / Path ends');
  if (!hasLeftBounds) problems.push('missing left-bounds value');

  return {
    ok: problems.length === 0,
    hash,
    toAddress,
    amount,
    token,
    fundsAmount,
    fundsToken,
    continuesAs,
    hasLeftBounds,
    problems
  };
}


function caseHopSolverValidateTool88RawRows(rows = []) {
  const dataRows = (Array.isArray(rows) ? rows : [])
    .map(caseHopSolverCloneRow)
    .filter(rowHasMeaningfulData)
    .filter((row, index) => !(index === 0 && caseHopSolverRowLooksLikeRawHeader(row)));
  const hashRows = dataRows.filter(row => normalizeCell(row?.[1]));
  const qualityRows = hashRows.map((row, index) => ({
    row,
    index,
    spreadsheetRow: index + 1,
    quality: caseHopSolverHashRowQuality(row)
  }));
  const incompleteHashRows = qualityRows.filter(item => !item.quality.ok);
  const badNullRows = dataRows.filter(caseHopSolverRowHasBadNullAmountToken);
  const usableRows = qualityRows.filter(item => item.quality.ok).map(item => item.row);
  const pathEndingOnlyWithoutAmounts = hashRows.filter((row) => {
    const hasPathEnd = /\bpath\s+ends\b/i.test(normalizeCell(row?.[16]));
    const amount = caseHopSolverParseAmount(row?.[5]);
    const token = caseHopSolverNormalizeToken(row?.[6]);
    return hasPathEnd && (!Number.isFinite(amount) || amount <= 0 || !token);
  });
  const problems = [];
  if (dataRows.length === 0) problems.push('No meaningful Tool 88 rows were returned.');
  if (hashRows.length === 0) problems.push('No transaction rows with Column B hashes were returned.');
  if (badNullRows.length > 0) problems.push(`${badNullRows.length} row${badNullRows.length === 1 ? '' : 's'} contain null/undefined/NaN amount-token text.`);
  if (usableRows.length === 0) problems.push('No returned hash row has the required To address, amount, token, funds-followed, left-bounds, and Continues As values.');
  if (incompleteHashRows.length > 0) {
    const examples = incompleteHashRows.slice(0, 3).map((item) => {
      const label = normalizeCell(item.row?.[0]) || caseHopSolverShortHash(item.row?.[1]) || `row ${item.spreadsheetRow}`;
      return `${label}: ${item.quality.problems.join(', ')}`;
    });
    problems.push(`${incompleteHashRows.length} transaction row${incompleteHashRows.length === 1 ? '' : 's'} are incomplete for Raw Data import. ${examples.join(' | ')}`);
  }
  if (pathEndingOnlyWithoutAmounts.length > 0) problems.push(`${pathEndingOnlyWithoutAmounts.length} path-ending row${pathEndingOnlyWithoutAmounts.length === 1 ? '' : 's'} are missing amount/token data.`);
  return {
    valid: problems.length === 0,
    rowCount: dataRows.length,
    hashRowCount: hashRows.length,
    usableRowCount: usableRows.length,
    incompleteHashRowCount: incompleteHashRows.length,
    badNullRowCount: badNullRows.length,
    pathEndingOnlyWithoutAmountsCount: pathEndingOnlyWithoutAmounts.length,
    incompleteHashRows: incompleteHashRows.slice(0, 10).map(item => ({
      rowIndex: item.index,
      pathLabel: normalizeCell(item.row?.[0]),
      hash: normalizeCell(item.row?.[1]),
      problems: item.quality.problems
    })),
    problems,
    summary: problems.join(' ')
  };
}

function caseHopSolverFirstNonEmptyEntryValue(entryRows = [], columnIndex = 0) {
  for (const entryRow of Array.isArray(entryRows) ? entryRows : []) {
    const value = normalizeCell(entryRow?.row?.[columnIndex]);
    if (value) return value;
  }
  return '';
}

function caseHopSolverEntryRowIndexes(entry = {}) {
  return (Array.isArray(entry.rows) ? entry.rows : [])
    .map(item => item?.rowIndex)
    .filter(index => Number.isInteger(index));
}

function caseHopSolverEntryContainsPathEnd(entry = {}) {
  return (Array.isArray(entry.rows) ? entry.rows : [])
    .some(item => /\bpath\s+ends\b/i.test(normalizeCell(item?.row?.[16])));
}

function caseHopSolverCreateAutomationProgress(overrides = {}) {
  return {
    active: false,
    status: 'idle',
    title: '',
    detail: '',
    action: '',
    stepCount: 0,
    researchedCount: 0,
    outputRows: 0,
    endingCount: 0,
    completeEndings: 0,
    openEndings: 0,
    flowCompleteConfirmations: 0,
    selectedLabel: '',
    selectedHash: '',
    selectedTarget: '',
    startedAtUtc: '',
    updatedAtUtc: '',
    finishedAtUtc: '',
    eventKey: '',
    events: [],
    ...(overrides || {})
  };
}

function caseHopSolverFormatProgressAction(value = '') {
  return normalizeCell(value).replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function caseHopSolverBuildProgressTitle(snapshot = {}, running = false) {
  if (snapshot.guardrail || snapshot.needsManualReview || snapshot.needsTargetSelection) return 'Tool 88 paused for review';
  if (snapshot.flowComplete) return 'Tool 88 sees the flow complete';
  if (running) return 'Tool 88 is tracing unresolved path endings';
  if (normalizeCell(snapshot.status)) return caseHopSolverFormatProgressAction(snapshot.status);
  return 'Tool 88 is preparing raw-data solving';
}

function caseHopSolverBuildProgressDetail(snapshot = {}, fallback = '') {
  const runState = snapshot.runState && typeof snapshot.runState === 'object' ? snapshot.runState : {};
  return normalizeCell(runState.detail)
    || normalizeCell(snapshot.message)
    || normalizeCell(fallback)
    || 'Tool 88 is hydrating the starting row, checking transaction signals, and building WayFinder-like raw rows.';
}

function caseHopSolverBuildProgressEventLabel(progress = {}, snapshot = {}) {
  const parts = [];
  if (progress.stepCount > 0) parts.push(`Step ${progress.stepCount}`);
  const action = caseHopSolverFormatProgressAction(progress.action || snapshot.integrationAction || snapshot.status);
  if (action) parts.push(action);
  if (progress.selectedLabel || progress.selectedHash) {
    parts.push([progress.selectedLabel ? `path ${progress.selectedLabel}` : '', progress.selectedHash ? caseHopSolverShortHash(progress.selectedHash) : ''].filter(Boolean).join(' · '));
  }
  if (progress.outputRows > 0) parts.push(`${progress.outputRows} raw row${progress.outputRows === 1 ? '' : 's'}`);
  if (snapshot.flowComplete) parts.push('flow complete signal');
  if (snapshot.guardrail || snapshot.needsManualReview || snapshot.needsTargetSelection) parts.push('review needed');
  return parts.filter(Boolean).join(' · ') || caseHopSolverBuildProgressDetail(snapshot, progress.detail);
}

function caseHopSolverFindPathEndingRow(entry = {}) {
  const rows = Array.isArray(entry.rows) ? entry.rows : [];
  return rows.find(item => /\bpath\s+ends\b/i.test(normalizeCell(item?.row?.[16]))) || rows[0] || null;
}

function caseHopSolverCandidateFromEntry(entry = {}, index = 0) {
  const endingRecord = caseHopSolverFindPathEndingRow(entry);
  const endingRow = Array.isArray(endingRecord?.row) ? endingRecord.row : [];
  const firstRow = Array.isArray(entry.rows?.[0]?.row) ? entry.rows[0].row : endingRow;
  const rowIndex = Number.isInteger(endingRecord?.rowIndex) ? endingRecord.rowIndex : Number.isInteger(entry.startRow) ? entry.startRow : -1;
  const amount = normalizeCell(endingRow[5]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 5);
  const token = caseHopSolverNormalizeToken(endingRow[6]) || caseHopSolverNormalizeToken(caseHopSolverFirstNonEmptyEntryValue(entry.rows, 6));
  const amountWithToken = caseHopSolverFormatAmountToken(amount, token);
  const fundsFollowed = normalizeCell(endingRow[12]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 12) || amountWithToken;
  const leftBoundsDisplay = normalizeCell(endingRow[17]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 17) || (amountWithToken ? `0 ${token}`.trim() : '0');
  const leftBoundsNumeric = normalizeCell(endingRow[18]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 18) || '0';

  return {
    id: `case-hop-solver-ending-${rowIndex}-${index}-${caseHopSolverNormalizeKey(entry.hash || endingRow[1]).slice(0, 16) || 'blank'}`,
    rowIndex,
    spreadsheetRowNumber: rowIndex >= 0 ? rowIndex + 1 : '',
    entryStartRowIndex: Number.isInteger(entry.startRow) ? entry.startRow : rowIndex,
    entryRowIndexes: caseHopSolverEntryRowIndexes(entry),
    pathLabel: normalizeCell(endingRow[0]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 0),
    hash: normalizeCell(entry.hash || endingRow[1] || firstRow[1]),
    shortHash: caseHopSolverShortHash(entry.hash || endingRow[1] || firstRow[1]),
    fromAddress: normalizeCell(endingRow[2]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 2),
    toAddress: normalizeCell(endingRow[3]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 3),
    utcDate: normalizeCell(endingRow[4]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 4),
    amount,
    token,
    systemNotes: normalizeCell(endingRow[9]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 9),
    attribution: normalizeCell(endingRow[10]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 10),
    analystNotes: normalizeCell(endingRow[11]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 11),
    fundsFollowed,
    priorBalance: normalizeCell(endingRow[13]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 13),
    fromHash: normalizeCell(endingRow[14]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 14),
    continuesAs: normalizeCell(endingRow[16]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 16),
    leftBoundsDisplay,
    leftBoundsNumeric,
    chainId: normalizeCell(endingRow[19]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 19),
    chainName: normalizeCell(endingRow[20]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 20),
    hopLogic: normalizeCell(endingRow[21]) || caseHopSolverFirstNonEmptyEntryValue(entry.rows, 21) || 'FIFO',
    isPathEnding: true,
    missingFundsFollowedFallback: !normalizeCell(endingRow[12]) && !!amountWithToken,
    missingLeftBoundsFallback: !normalizeCell(endingRow[17]) && !normalizeCell(endingRow[18]),
    row: caseHopSolverCloneRow(endingRow)
  };
}

export const caseBuddyHopSolverMethods = {
  getCaseHopSolverTool88Ref() {
    const ref = this.$refs.caseHopSolverTool88;
    return Array.isArray(ref) ? ref[0] : ref;
  },

  getCaseHopSolverRowsForTool88() {
    this.ensureCaseWorkbookScaffold?.({ silent: true });
    return normalizeCaseMatrix(this.getMatrixForLogicalSheet?.('main') || [], CASE_DEFAULT_ROWS, CASE_DEFAULT_COLS);
  },

  getCaseHopSolverUnresolvedCandidates() {
    const mainData = this.getCaseHopSolverRowsForTool88();
    return parseCaseEntries(mainData)
      .filter(caseHopSolverEntryContainsPathEnd)
      .map(caseHopSolverCandidateFromEntry)
      .filter(candidate => candidate.hash && candidate.isPathEnding);
  },

  normalizeCaseHopSolverCandidate(candidate = {}) {
    const source = candidate || {};
    const amount = normalizeCell(source.amount);
    const token = caseHopSolverNormalizeToken(source.token);
    const amountWithToken = caseHopSolverFormatAmountToken(amount, token);
    const fundsFollowed = normalizeCell(source.fundsFollowed) || amountWithToken;
    const leftBoundsDisplay = normalizeCell(source.leftBoundsDisplay || source.leftBounds) || (amountWithToken ? `0 ${token}`.trim() : '0');
    const leftBoundsNumeric = normalizeCell(source.leftBoundsNumeric) || '0';
    return {
      id: normalizeCell(source.id),
      rowIndex: Number.isInteger(source.rowIndex) ? source.rowIndex : null,
      spreadsheetRowNumber: source.spreadsheetRowNumber || (Number.isInteger(source.rowIndex) ? source.rowIndex + 1 : ''),
      pathLabel: normalizeCell(source.pathLabel),
      hash: normalizeCell(source.hash || source.transactionHash || source.txHash),
      txHash: normalizeCell(source.hash || source.transactionHash || source.txHash),
      shortHash: normalizeCell(source.shortHash) || caseHopSolverShortHash(source.hash || source.transactionHash || source.txHash),
      fromAddress: normalizeCell(source.fromAddress),
      toAddress: normalizeCell(source.toAddress),
      targetAddress: normalizeCell(source.targetAddress || source.toAddress),
      utcDate: normalizeCell(source.utcDate),
      amount,
      token,
      systemNotes: normalizeCell(source.systemNotes),
      attribution: normalizeCell(source.attribution),
      analystNotes: normalizeCell(source.analystNotes),
      fundsFollowed,
      priorBalance: normalizeCell(source.priorBalance),
      fromHash: normalizeCell(source.fromHash),
      continuesAs: normalizeCell(source.continuesAs),
      leftBoundsDisplay,
      leftBoundsNumeric,
      chainId: normalizeCell(source.chainId),
      chainName: normalizeCell(source.chainName),
      hopLogic: normalizeCell(source.hopLogic) || 'FIFO',
      isPathEnding: !!source.isPathEnding,
      missingFundsFollowedFallback: !normalizeCell(source.fundsFollowed) && !!amountWithToken,
      missingLeftBoundsFallback: !normalizeCell(source.leftBoundsDisplay || source.leftBounds) && !normalizeCell(source.leftBoundsNumeric)
    };
  },

  resetCaseAutomationTool88Activity() {
    this.caseAutomationTool88Activity = createDefaultCaseAutomationTool88Activity();
    this.caseAutomationTool88Progress = caseHopSolverActivityToProgress(this.caseAutomationTool88Activity);
    return this.caseAutomationTool88Activity;
  },

  startCaseAutomationTool88Activity(options = {}) {
    const now = caseHopSolverNowUtc();
    const base = createDefaultCaseAutomationTool88Activity();
    const initialMessage = normalizeCell(options.message) || 'Tool 88 is preparing to gather raw data for the current main transaction.';
    this.caseAutomationTool88Activity = {
      ...base,
      active: true,
      startedAtUtc: now,
      updatedAtUtc: now,
      status: normalizeCell(options.status) || 'starting',
      title: normalizeCell(options.title) || 'Starting Tool 88',
      message: initialMessage,
      detail: normalizeCell(options.detail),
      targetLabel: normalizeCell(options.targetLabel),
      targetHash: normalizeCell(options.targetHash),
      targetAddress: normalizeCell(options.targetAddress),
      log: [{
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        atUtc: now,
        message: initialMessage
      }]
    };
    this.caseAutomationTool88Progress = caseHopSolverActivityToProgress(this.caseAutomationTool88Activity);
    if (this.caseAutomation?.active) {
      this.setCaseAutomationStatus?.('Solving raw data with Tool 88…', initialMessage);
    }
    return this.caseAutomationTool88Activity;
  },

  finishCaseAutomationTool88Activity(options = {}) {
    const now = caseHopSolverNowUtc();
    const current = this.caseAutomationTool88Activity || createDefaultCaseAutomationTool88Activity();
    const message = normalizeCell(options.message) || current.message || 'Tool 88 raw-data solving finished.';
    const log = Array.isArray(current.log) ? current.log.slice() : [];
    const logKey = `finish|${normalizeCell(options.status)}|${message}|${options.rowsAppended || 0}`;
    if (current.lastLogKey !== logKey) {
      log.unshift({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        atUtc: now,
        message
      });
    }
    this.caseAutomationTool88Activity = {
      ...current,
      active: false,
      updatedAtUtc: now,
      completedAtUtc: now,
      status: normalizeCell(options.status) || current.status || 'finished',
      title: normalizeCell(options.title) || current.title || 'Tool 88 finished',
      message,
      detail: normalizeCell(options.detail) || current.detail || '',
      rawRows: Math.max(Number(current.rawRows) || 0, Number(options.rawRows) || 0),
      rowsAppended: Math.max(Number(current.rowsAppended) || 0, Number(options.rowsAppended) || 0),
      lastLogKey: logKey,
      log: log.slice(0, 8)
    };
    this.caseAutomationTool88Progress = {
      ...caseHopSolverActivityToProgress(this.caseAutomationTool88Activity),
      outputRows: Math.max(Number(current.rawRows) || 0, Number(options.rawRows) || 0, Number(options.rowsAppended) || 0),
      rowsAppended: Math.max(Number(current.rowsAppended) || 0, Number(options.rowsAppended) || 0)
    };
    return this.caseAutomationTool88Activity;
  },

  updateCaseAutomationTool88ActivityFromSnapshot(snapshot = {}, options = {}) {
    const safeSnapshot = snapshot && typeof snapshot === 'object' ? snapshot : {};
    const runState = safeSnapshot.runState && typeof safeSnapshot.runState === 'object' ? safeSnapshot.runState : {};
    const flow = safeSnapshot.flowCompletion && typeof safeSnapshot.flowCompletion === 'object' ? safeSnapshot.flowCompletion : {};
    const current = this.caseAutomationTool88Activity || createDefaultCaseAutomationTool88Activity();
    const now = caseHopSolverNowUtc();
    const selected = caseHopSolverSelectedEntryTarget(safeSnapshot);
    const outputRows = Array.isArray(safeSnapshot.outputRows) ? safeSnapshot.outputRows.filter(rowHasMeaningfulData) : [];
    const rawRows = outputRows.length > 0 ? outputRows.length : Number(current.rawRows) || 0;
    const stepCount = Math.max(Number(current.stepCount) || 0, Number(runState.stepCount) || 0);
    const researchedCount = Math.max(Number(current.researchedCount) || 0, Array.isArray(runState.researchedEntryKeys) ? runState.researchedEntryKeys.length : 0);
    const entryCount = Number(flow.totalEntries) || Number(current.entryCount) || 0;
    const endingCount = Number(flow.endingCount) || Number(current.endingCount) || 0;
    const completeCount = Number(flow.completeCount) || Number(current.completeCount) || 0;
    const unresolvedCount = Number.isFinite(Number(flow.incompleteCount)) ? Number(flow.incompleteCount) : Number(current.unresolvedCount) || 0;
    const flowCompleteConfirmations = Math.max(Number(current.flowCompleteConfirmations) || 0, Number(runState.flowCompleteConfirmations) || 0);
    const message = caseHopSolverSnapshotMessage(safeSnapshot, runState);
    const title = caseHopSolverSnapshotTitle(safeSnapshot, runState);
    const eventName = normalizeCell(options.eventName) || normalizeCell(safeSnapshot.eventName) || 'case-buddy-progress';
    const status = normalizeCell(safeSnapshot.status || runState.status || current.status || 'running');
    const integrationAction = normalizeCell(safeSnapshot.integrationAction || current.integrationAction);
    const guardrailMessage = normalizeCell(safeSnapshot.guardrail?.message || safeSnapshot.guardrail?.reason || current.guardrailMessage);
    const logKey = [eventName, status, integrationAction, stepCount, unresolvedCount, completeCount, rawRows, flowCompleteConfirmations, message].join('|');
    const log = Array.isArray(current.log) ? current.log.slice() : [];

    if (current.lastLogKey !== logKey) {
      const prefix = stepCount > 0 ? `Round ${stepCount}: ` : '';
      log.unshift({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        atUtc: now,
        message: caseHopSolverCompactMessage(`${prefix}${message}`, 145)
      });
    }

    const active = !!(
      options.active === true ||
      runState.active ||
      this.caseHopSolverAutomationRunning ||
      this.caseHopSolverRunning ||
      (this.caseAutomation?.active && !safeSnapshot.flowComplete) ||
      (this.caseAutomation?.active && !safeSnapshot.guardrail && safeSnapshot.flowComplete && !current.rowsAppended)
    );

    this.caseAutomationTool88Activity = {
      ...current,
      active,
      startedAtUtc: current.startedAtUtc || now,
      updatedAtUtc: now,
      status,
      title,
      message,
      detail: normalizeCell(runState.detail) || message,
      eventName,
      integrationAction,
      stepCount,
      researchedCount,
      entryCount,
      endingCount,
      completeCount,
      unresolvedCount,
      flowComplete: !!safeSnapshot.flowComplete,
      flowCompleteConfirmations,
      rawRows,
      targetLabel: selected.targetLabel || current.targetLabel,
      targetHash: selected.targetHash || current.targetHash,
      targetAddress: selected.targetAddress || current.targetAddress,
      guardrailMessage,
      lastLogKey: logKey,
      log: log.slice(0, 8)
    };
    this.caseAutomationTool88Progress = caseHopSolverActivityToProgress(this.caseAutomationTool88Activity);

    if (this.caseAutomation?.active) {
      const stepTag = String(this.caseAutomation.lastPlan?.stepTag || this.recommendedWorkflowStep?.tag || '').toUpperCase();
      if (stepTag === 'RAW_DATA_FETCH' || /raw data|tool\s*88|hop solver/i.test(`${this.caseAutomation.status || ''} ${this.caseAutomation.detail || ''}`)) {
        const statusLabel = safeSnapshot.flowComplete
          ? 'Confirming Tool 88 raw-data flow…'
          : guardrailMessage
            ? 'Tool 88 needs review…'
            : 'Solving raw data with Tool 88…';
        const detailParts = [message];
        if (stepCount > 0) detailParts.push(`Round ${stepCount}.`);
        if (endingCount > 0) detailParts.push(`${Math.max(0, unresolvedCount)} unresolved of ${endingCount} ending${endingCount === 1 ? '' : 's'}.`);
        if (flowCompleteConfirmations > 0) detailParts.push(`Completion check ${flowCompleteConfirmations}.`);
        this.setCaseAutomationStatus?.(statusLabel, detailParts.filter(Boolean).join(' '));
      }
    }

    const shouldLogAutomationMilestone = !!this.caseAutomation?.active && current.lastAutomationLogKey !== logKey && (
      stepCount === 1 ||
      (stepCount > 1 && stepCount % 3 === 0) ||
      safeSnapshot.flowComplete ||
      !!safeSnapshot.guardrail ||
      rawRows > Number(current.rawRows || 0)
    );
    if (shouldLogAutomationMilestone) {
      this.caseAutomationTool88Activity.lastAutomationLogKey = logKey;
      this.addCaseAutomationLog?.(`Tool 88: ${caseHopSolverCompactMessage(message, 110)}`, {
        eventName,
        status,
        stepCount,
        unresolvedCount,
        rawRows
      });
    }

    return this.caseAutomationTool88Activity;
  },


  resetCaseAutomationTool88Progress(overrides = {}) {
    this.caseAutomationTool88Progress = caseHopSolverCreateAutomationProgress(overrides);
    return this.caseAutomationTool88Progress;
  },

  updateCaseAutomationTool88ProgressFromSnapshot(snapshot = {}, options = {}) {
    const safeSnapshot = snapshot && typeof snapshot === 'object' ? snapshot : {};
    const current = this.caseAutomationTool88Progress || caseHopSolverCreateAutomationProgress();
    const runState = safeSnapshot.runState && typeof safeSnapshot.runState === 'object' ? safeSnapshot.runState : {};
    const flow = safeSnapshot.flowCompletion && typeof safeSnapshot.flowCompletion === 'object' ? safeSnapshot.flowCompletion : {};
    const selected = safeSnapshot.selectedEntry || this.caseHopSolverCandidate || {};
    const rows = Array.isArray(options.rows) ? options.rows : this.getCaseHopSolverSnapshotDataRows(safeSnapshot);
    const outputRows = rows.filter(rowHasMeaningfulData).length;
    const researchedCount = Array.isArray(runState.researchedEntryKeys) ? runState.researchedEntryKeys.length : Number(current.researchedCount) || 0;
    const stepCount = Math.max(Number(current.stepCount) || 0, Number(runState.stepCount) || 0);
    const selectedHash = normalizeCell(selected.txHash || selected.hash || this.caseHopSolverCandidate?.hash);
    const selectedTarget = normalizeCell(safeSnapshot.selectedTargetAddress || selected.toAddress || selected.targetAddress || this.caseHopSolverCandidate?.toAddress);
    const progress = {
      ...current,
      active: true,
      status: normalizeCell(safeSnapshot.status) || current.status || 'working',
      title: caseHopSolverBuildProgressTitle(safeSnapshot, !!(this.caseHopSolverRunning || this.caseHopSolverAutomationRunning)),
      detail: caseHopSolverBuildProgressDetail(safeSnapshot, this.caseHopSolverStatus || this.caseAutomation?.detail),
      action: normalizeCell(safeSnapshot.integrationAction || safeSnapshot.decision?.action) || current.action,
      stepCount,
      researchedCount,
      outputRows: Math.max(Number(current.outputRows) || 0, outputRows),
      totalEntries: Math.max(Number(current.totalEntries) || 0, Number(flow.totalEntries) || 0),
      endingCount: Math.max(Number(current.endingCount) || 0, Number(flow.endingCount) || 0),
      completeEndings: Math.max(Number(current.completeEndings) || 0, Number(flow.completeCount) || 0),
      openEndings: Number.isFinite(Number(flow.incompleteCount)) ? Number(flow.incompleteCount) : Number(current.openEndings) || 0,
      flowCompleteConfirmations: Math.max(Number(current.flowCompleteConfirmations) || 0, Number(runState.flowCompleteConfirmations) || 0, Number(this.caseHopSolverFlowCompleteConfirmations) || 0),
      selectedLabel: normalizeCell(selected.pathLabel) || current.selectedLabel,
      selectedHash: selectedHash || current.selectedHash,
      selectedTarget: selectedTarget || current.selectedTarget,
      startedAtUtc: current.startedAtUtc || new Date().toISOString(),
      updatedAtUtc: new Date().toISOString(),
      finishedAtUtc: safeSnapshot.flowComplete || safeSnapshot.guardrail || safeSnapshot.needsManualReview || safeSnapshot.needsTargetSelection ? new Date().toISOString() : current.finishedAtUtc
    };
    const eventLabel = caseHopSolverBuildProgressEventLabel(progress, safeSnapshot);
    const eventKey = [
      progress.status,
      progress.action,
      progress.stepCount,
      progress.researchedCount,
      progress.outputRows,
      progress.completeEndings,
      progress.openEndings,
      safeSnapshot.flowComplete ? 'complete' : '',
      safeSnapshot.guardrail?.reason || ''
    ].join('|');
    progress.eventKey = eventKey;
    if (eventLabel && eventKey !== current.eventKey) {
      progress.events = [
        { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, label: eventLabel, atUtc: progress.updatedAtUtc },
        ...(Array.isArray(current.events) ? current.events : [])
      ].slice(0, 7);
    }
    this.caseAutomationTool88Progress = progress;

    const rawFetchActive = this.caseAutomation?.active && String(this.caseAutomation.lastPlan?.stepTag || '').toUpperCase() === 'RAW_DATA_FETCH';
    if (rawFetchActive) {
      const detail = progress.detail || eventLabel || 'Tool 88 is solving raw-data coverage.';
      this.setCaseAutomationStatus?.('Solving raw data with Tool 88…', detail);
      const nowMs = Date.now();
      const important = !!(safeSnapshot.flowComplete || safeSnapshot.guardrail || safeSnapshot.needsManualReview || safeSnapshot.needsTargetSelection || progress.outputRows > 0);
      if (eventLabel && eventKey !== this.caseAutomation.rawSolverLastProgressKey && (important || nowMs - (Number(this.caseAutomation.rawSolverLastLogAtMs) || 0) > 1400)) {
        this.addCaseAutomationLog?.(eventLabel, { source: 'tool88', status: progress.status, action: progress.action, stepCount: progress.stepCount });
        this.caseAutomation.rawSolverLastProgressKey = eventKey;
        this.caseAutomation.rawSolverLastLogAtMs = nowMs;
      }
      this.caseAutomation.rawSolverLastSnapshotAtUtc = progress.updatedAtUtc;
    }
    return progress;
  },

  resetCaseHopSolverState(options = {}) {
    if (this.caseHopSolverRunning && !options.force) return false;
    if (options.close !== false) this.showCaseHopSolverModal = false;
    this.caseHopSolverCandidate = null;
    this.caseHopSolverStatus = '';
    this.caseHopSolverStatusTitle = 'Solve hop';
    this.caseHopSolverRunning = false;
    this.caseHopSolverStopRequested = false;
    this.caseHopSolverSnapshot = null;
    this.caseHopSolverOutputRows = [];
    this.caseHopSolverOutputTsv = '';
    this.caseHopSolverLastEvent = '';
    this.caseHopSolverRawApplyStatus = '';
    this.caseHopSolverFlowCompleteFirstSeenAtMs = 0;
    this.caseHopSolverFlowCompleteConfirmations = 0;
    this.caseInvestigationHopSolverPlaceholder = {
      open: false,
      busy: false,
      stopRequested: false,
      status: '',
      detail: '',
      candidateId: '',
      candidateSnapshot: null,
      startedAtUtc: '',
      stoppedAtUtc: '',
      completedAtUtc: ''
    };
    return true;
  },

  async openCaseHopSolverModal(options = {}) {
    if (this.caseInvestigationVisualizerIsLocked?.()) return this.showCaseInvestigationVisualizerLockedToast();
    const candidate = options.candidate ? this.normalizeCaseHopSolverCandidate(options.candidate) : null;
    this.showCaseHopSolverModal = true;
    this.caseHopSolverCandidate = candidate;
    this.caseHopSolverStatusTitle = candidate ? 'Solve selected hop' : 'Solve next unresolved hop';
    this.caseHopSolverStatus = candidate
      ? 'Loading the selected path ending into Tool 88.'
      : 'Opening Tool 88. Use Select next unresolved or Auto solve to choose the next path ending.';
    this.caseHopSolverStopRequested = false;
    this.caseHopSolverSnapshot = null;
    this.caseHopSolverOutputRows = [];
    this.caseHopSolverOutputTsv = '';
    this.caseHopSolverLastEvent = '';
    this.caseHopSolverRawApplyStatus = '';
    this.caseHopSolverStartInAdvancedView = options.startInAdvancedView === true || this.caseHopSolverViewMode === 'advanced';
    this.caseInvestigationHopSolverPlaceholder = {
      open: true,
      busy: false,
      stopRequested: false,
      status: 'Tool 88 readying',
      detail: this.caseHopSolverStatus,
      candidateId: candidate?.id || '',
      candidateSnapshot: candidate,
      startedAtUtc: '',
      stoppedAtUtc: '',
      completedAtUtc: ''
    };
    await this.$nextTick?.();
    if (candidate && options.autoLoad !== false) {
      return this.loadCaseHopSolverSelectedCandidate({ candidate, includeRows: true });
    }
    if (!candidate && options.selectNextUnresolved) {
      return this.selectCaseHopSolverNextUnresolved({ includeRows: true });
    }
    return true;
  },

  getCaseHopSolverCandidateFromInvestigationVisualizerSelection(options = {}) {
    const existing = this.caseInvestigationVisualizerSelectedCandidate;
    if (existing?.isPathEnding) return this.normalizeCaseHopSolverCandidate(existing);

    const node = this.caseInvestigationVisualizerSelectedNode
      || (this.caseInvestigationVisualizerSelectedNodeId
        ? this.caseInvestigationVisualizerNodeMap?.get?.(this.caseInvestigationVisualizerSelectedNodeId)
        : null);
    const rowIndexes = this.getCaseInvestigationVisualizerRowIndexesForNode?.(node) || [];
    if (!rowIndexes.length) return null;

    const rowIndexSet = new Set(rowIndexes);
    const mainData = this.getCaseHopSolverRowsForTool88();
    const entries = parseCaseEntries(mainData);
    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index];
      const entryRows = Array.isArray(entry?.rows) ? entry.rows : [];
      const containsSelectedRow = entryRows.some(item => rowIndexSet.has(item?.rowIndex));
      if (!containsSelectedRow || !caseHopSolverEntryContainsPathEnd(entry)) continue;
      return this.normalizeCaseHopSolverCandidate(caseHopSolverCandidateFromEntry(entry, index));
    }

    if (!options.quiet) {
      this.showCaseToast('Select a transaction node whose Main Data entry currently says Path ends.');
    }
    return null;
  },

  openCaseHopSolverFromInvestigationVisualizer() {
    const candidate = this.getCaseHopSolverCandidateFromInvestigationVisualizerSelection();
    if (!candidate) {
      this.showCaseToast('Select a Path ends transaction/output node before opening Solve hop.');
      return false;
    }
    if (!candidate.isPathEnding) {
      this.showCaseToast('Solve hop is reserved for selected Path ends rows. Use Prune for non-ending outputs.');
      return false;
    }
    return this.openCaseHopSolverModal({ candidate, source: 'investigation-visualizer' });
  },

  // Backwards-compatible method name used by the first placeholder pass.
  openCaseHopSolverPlaceholderFromInvestigationVisualizer() {
    return this.openCaseHopSolverFromInvestigationVisualizer();
  },

  async openCaseHopSolverNextUnresolved(options = {}) {
    const candidates = this.getCaseHopSolverUnresolvedCandidates();
    const candidate = candidates[0] || null;
    if (!candidate) {
      this.showCaseToast('No unresolved Path ends rows were found in Main Data.');
      return false;
    }
    return this.openCaseHopSolverModal({ ...options, candidate, source: 'next-unresolved' });
  },

  async loadCaseHopSolverSelectedCandidate(options = {}) {
    const tool = this.getCaseHopSolverTool88Ref();
    const candidate = this.normalizeCaseHopSolverCandidate(options.candidate || this.caseHopSolverCandidate || {});
    if (!tool?.caseBuddyOpenSelectedHop) {
      this.caseHopSolverStatus = 'Tool 88 is still mounting. Try again after the modal finishes opening.';
      return false;
    }
    if (!candidate?.hash || !candidate?.isPathEnding) {
      this.caseHopSolverStatus = 'A valid Path ends row is required before Tool 88 can solve a hop.';
      return false;
    }
    this.caseHopSolverCandidate = candidate;
    this.caseHopSolverRunning = true;
    this.caseHopSolverStatusTitle = 'Loading selected hop';
    this.caseHopSolverStatus = 'Tool 88 is loading the current Main Data rows and selected path ending.';
    this.caseInvestigationHopSolverPlaceholder = {
      ...(this.caseInvestigationHopSolverPlaceholder || {}),
      open: true,
      busy: true,
      stopRequested: false,
      status: 'Loading selected hop',
      detail: this.caseHopSolverStatus,
      candidateId: candidate.id || '',
      candidateSnapshot: candidate,
      startedAtUtc: caseHopSolverNowUtc(),
      stoppedAtUtc: '',
      completedAtUtc: ''
    };
    try {
      const snapshot = await tool.caseBuddyOpenSelectedHop(candidate, {
        rows: this.getCaseHopSolverRowsForTool88(),
        includeRows: options.includeRows !== false,
        hopLogic: candidate.hopLogic || 'FIFO',
        traceDirection: options.traceDirection || 'forward'
      });
      this.handleCaseHopSolverSnapshot(snapshot, { eventName: 'case-buddy-status' });
      return snapshot;
    } finally {
      this.caseHopSolverRunning = false;
      this.caseInvestigationHopSolverPlaceholder = {
        ...(this.caseInvestigationHopSolverPlaceholder || {}),
        busy: false,
        completedAtUtc: caseHopSolverNowUtc()
      };
    }
  },

  async selectCaseHopSolverNextUnresolved(options = {}) {
    const tool = this.getCaseHopSolverTool88Ref();
    if (!tool?.caseBuddySelectNextUnresolvedHop) {
      this.caseHopSolverStatus = 'Tool 88 is still mounting. Try again after the modal finishes opening.';
      return false;
    }
    this.caseHopSolverRunning = true;
    this.caseHopSolverStatusTitle = 'Selecting next unresolved';
    this.caseHopSolverStatus = 'Tool 88 is looking for the next unresolved Path ends row.';
    try {
      const snapshot = await tool.caseBuddySelectNextUnresolvedHop({
        rows: this.getCaseHopSolverRowsForTool88(),
        includeRows: options.includeRows !== false,
        speedMultiplier: this.caseAutomation?.speedMultiplier || 1
      });
      this.handleCaseHopSolverSnapshot(snapshot, { eventName: snapshot?.flowComplete ? 'case-buddy-flow-complete' : 'case-buddy-status' });
      return snapshot;
    } finally {
      this.caseHopSolverRunning = false;
    }
  },

  async runCaseHopSolverNextAction(options = {}) {
    const tool = this.getCaseHopSolverTool88Ref();
    if (!tool?.caseBuddyRunNextAction) {
      this.caseHopSolverStatus = 'Tool 88 is still mounting. Try again after the modal finishes opening.';
      return false;
    }
    this.caseHopSolverRunning = true;
    this.caseHopSolverStatusTitle = 'Solving next action';
    this.caseHopSolverStatus = 'Tool 88 is running one guided Case Buddy action.';
    this.caseInvestigationHopSolverPlaceholder = {
      ...(this.caseInvestigationHopSolverPlaceholder || {}),
      open: true,
      busy: true,
      stopRequested: false,
      status: 'Running Tool 88 action',
      detail: this.caseHopSolverStatus,
      startedAtUtc: this.caseInvestigationHopSolverPlaceholder?.startedAtUtc || caseHopSolverNowUtc(),
      stoppedAtUtc: '',
      completedAtUtc: ''
    };
    try {
      const snapshot = await tool.caseBuddyRunNextAction({
        includeRows: options.includeRows !== false,
        speedMultiplier: this.caseAutomation?.speedMultiplier || 1,
        maxAutoLowValue: options.maxAutoLowValue
      });
      this.handleCaseHopSolverSnapshot(snapshot, { eventName: snapshot?.flowComplete ? 'case-buddy-flow-complete' : snapshot?.needsManualReview || snapshot?.guardrail ? 'case-buddy-guardrail' : 'case-buddy-result' });
      return snapshot;
    } finally {
      this.caseHopSolverRunning = false;
      this.caseInvestigationHopSolverPlaceholder = {
        ...(this.caseInvestigationHopSolverPlaceholder || {}),
        busy: false,
        completedAtUtc: caseHopSolverNowUtc()
      };
    }
  },

  async runCaseHopSolverAutomationLoop(options = {}) {
    const tool = this.getCaseHopSolverTool88Ref();
    if (!tool?.caseBuddyRunAutomationLoop) {
      this.caseHopSolverStatus = 'Tool 88 is still mounting. Try again after the modal finishes opening.';
      return false;
    }
    this.caseHopSolverRunning = true;
    this.caseHopSolverAutomationRunning = true;
    this.caseHopSolverStopRequested = false;
    this.caseHopSolverStatusTitle = 'Auto-solving unresolved hops';
    this.caseHopSolverStatus = 'Tool 88 automation is running with Case Buddy guardrails. Use Request stop to halt safely.';
    this.caseInvestigationHopSolverPlaceholder = {
      ...(this.caseInvestigationHopSolverPlaceholder || {}),
      open: true,
      busy: true,
      stopRequested: false,
      status: 'Tool 88 automation running',
      detail: this.caseHopSolverStatus,
      startedAtUtc: caseHopSolverNowUtc(),
      stoppedAtUtc: '',
      completedAtUtc: ''
    };
    try {
      const snapshot = await tool.caseBuddyRunAutomationLoop({
        includeRows: options.includeRows !== false,
        speedMultiplier: this.caseAutomation?.speedMultiplier || 1,
        ...(options.guardrails || {})
      });
      this.handleCaseHopSolverSnapshot(snapshot, { eventName: snapshot?.flowComplete ? 'case-buddy-flow-complete' : snapshot?.guardrail || snapshot?.needsManualReview ? 'case-buddy-guardrail' : 'case-buddy-result' });
      return snapshot;
    } finally {
      this.caseHopSolverRunning = false;
      this.caseHopSolverAutomationRunning = false;
      this.caseInvestigationHopSolverPlaceholder = {
        ...(this.caseInvestigationHopSolverPlaceholder || {}),
        busy: false,
        completedAtUtc: caseHopSolverNowUtc()
      };
    }
  },

  requestCaseHopSolverStop() {
    const tool = this.getCaseHopSolverTool88Ref();
    this.caseHopSolverStopRequested = true;
    this.caseHopSolverStatusTitle = 'Stop requested';
    this.caseHopSolverStatus = 'Stop requested. Tool 88 will halt at the next safe point and leave any output in review state.';
    this.caseInvestigationHopSolverPlaceholder = {
      ...(this.caseInvestigationHopSolverPlaceholder || {}),
      stopRequested: true,
      status: 'Stop requested',
      detail: this.caseHopSolverStatus,
      stoppedAtUtc: caseHopSolverNowUtc()
    };
    tool?.caseBuddyRequestAutomationStop?.('Case Buddy requested stop from the embedded Solve Hop modal.');
    return true;
  },

  // Backwards-compatible method name from the placeholder pass.
  requestCaseHopSolverPlaceholderStop() {
    return this.requestCaseHopSolverStop();
  },

  cancelCaseHopSolverModal() {
    if (this.caseHopSolverRunning || this.caseInvestigationHopSolverPlaceholder?.busy) {
      this.showCaseToast('Tool 88 is currently solving. Request stop before closing the modal.');
      return false;
    }
    return this.resetCaseHopSolverState({ close: true });
  },

  cancelCaseHopSolverPlaceholder() {
    return this.cancelCaseHopSolverModal();
  },

  setCaseHopSolverViewMode(mode = 'simple') {
    const normalized = mode === 'advanced' ? 'advanced' : 'simple';
    this.caseHopSolverViewMode = normalized;
    const tool = this.getCaseHopSolverTool88Ref();
    if (tool) {
      if (normalized === 'advanced') {
        tool.simpleViewOpen = false;
        tool.setSimpleViewBodyLock?.(false);
      } else if (typeof tool.openSimpleView === 'function') {
        tool.openSimpleView();
      } else {
        tool.simpleViewOpen = true;
        tool.setSimpleViewBodyLock?.(true);
      }
    }
  },

  handleCaseHopSolverProgress(snapshot = {}) {
    const safeSnapshot = snapshot && typeof snapshot === 'object' ? snapshot : {};
    this.caseHopSolverSnapshot = {
      ...(this.caseHopSolverSnapshot || {}),
      ...safeSnapshot,
      outputRows: Array.isArray(safeSnapshot.outputRows) && safeSnapshot.outputRows.length
        ? safeSnapshot.outputRows
        : (this.caseHopSolverSnapshot?.outputRows || [])
    };
    this.caseHopSolverLastEvent = 'case-buddy-progress';
    this.caseHopSolverStatusTitle = this.describeCaseHopSolverSnapshotTitle(safeSnapshot);
    this.caseHopSolverStatus = safeSnapshot.message || this.caseHopSolverStatus || 'Tool 88 is still solving this raw-data flow.';
    this.updateCaseAutomationTool88ActivityFromSnapshot(safeSnapshot, { eventName: 'case-buddy-progress', active: true });
    return safeSnapshot;
  },

  handleCaseHopSolverStatus(snapshot = {}) {
    return this.handleCaseHopSolverSnapshot(snapshot, { eventName: 'case-buddy-status' });
  },

  handleCaseHopSolverResult(snapshot = {}) {
    return this.handleCaseHopSolverSnapshot(snapshot, { eventName: 'case-buddy-result' });
  },

  handleCaseHopSolverGuardrail(snapshot = {}) {
    return this.handleCaseHopSolverSnapshot(snapshot, { eventName: 'case-buddy-guardrail' });
  },

  handleCaseHopSolverFlowComplete(snapshot = {}) {
    return this.handleCaseHopSolverSnapshot(snapshot, { eventName: 'case-buddy-flow-complete' });
  },

  handleCaseHopSolverSnapshot(snapshot = {}, options = {}) {
    const safeSnapshot = snapshot && typeof snapshot === 'object' ? snapshot : {};
    this.caseHopSolverSnapshot = safeSnapshot;
    this.caseHopSolverLastEvent = options.eventName || safeSnapshot.status || '';
    const rows = this.getCaseHopSolverSnapshotDataRows(safeSnapshot);
    this.caseHopSolverOutputRows = rows;
    this.caseHopSolverOutputTsv = rows.length ? buildTsv(rows) : normalizeCell(safeSnapshot.outputTsv);
    const rawValidation = caseHopSolverValidateTool88RawRows(rows);
    this.caseHopSolverOutputValidation = rawValidation;
    if (rows.length > 0 && !rawValidation.valid) {
      this.caseHopSolverRawApplyStatus = `Tool 88 output is not ready to import yet. ${rawValidation.summary}`;
    }
    this.caseHopSolverStatusTitle = this.describeCaseHopSolverSnapshotTitle(safeSnapshot);
    this.caseHopSolverStatus = safeSnapshot.message || this.caseHopSolverStatus || 'Tool 88 updated the Case Buddy solve-hop state.';
    if (safeSnapshot.selectedEntry && !this.caseHopSolverCandidate) {
      this.caseHopSolverCandidate = this.normalizeCaseHopSolverCandidate({
        pathLabel: safeSnapshot.selectedEntry.pathLabel,
        hash: safeSnapshot.selectedEntry.txHash,
        fromAddress: safeSnapshot.selectedEntry.fromAddress,
        toAddress: safeSnapshot.selectedEntry.toAddress,
        rowIndex: Number.isInteger(safeSnapshot.selectedEntry.startRowIndex) ? safeSnapshot.selectedEntry.startRowIndex : null,
        spreadsheetRowNumber: safeSnapshot.selectedEntry.spreadsheetRowNumber,
        fundsFollowed: safeSnapshot.selectedEntry.fundsFollowed,
        leftBoundsDisplay: safeSnapshot.selectedEntry.leftBoundsDisplay,
        leftBoundsNumeric: safeSnapshot.selectedEntry.leftBoundsNumeric,
        chainId: safeSnapshot.selectedEntry.chainId,
        chainName: safeSnapshot.selectedEntry.chainName,
        hopLogic: safeSnapshot.selectedEntry.hopLogic,
        isPathEnding: true
      });
    }
    if (safeSnapshot.flowComplete) {
      this.caseHopSolverFlowCompleteConfirmations += 1;
      if (!this.caseHopSolverFlowCompleteFirstSeenAtMs) this.caseHopSolverFlowCompleteFirstSeenAtMs = Date.now();
    }
    this.updateCaseAutomationTool88ActivityFromSnapshot(safeSnapshot, { eventName: options.eventName || safeSnapshot.status || 'case-buddy-status' });
    return safeSnapshot;
  },

  describeCaseHopSolverSnapshotTitle(snapshot = {}) {
    const status = normalizeCell(snapshot.status);
    if (snapshot.flowComplete) return 'Flow appears complete';
    if (snapshot.guardrail) return 'Automation paused for review';
    if (snapshot.needsTargetSelection) return 'Target selection needed';
    if (snapshot.needsManualReview) return 'Manual review needed';
    if (status === 'result-ready') return 'Solver result ready';
    if (status === 'decision-ready') return 'Decision ready';
    if (status === 'selected-hop-ready') return 'Selected hop ready';
    if (status === 'next-unresolved-selected') return 'Next unresolved selected';
    if (status === 'busy') return 'Tool 88 is busy';
    return this.caseHopSolverStatusTitle || 'Solve hop';
  },

  getCaseHopSolverSnapshotDataRows(snapshot = this.caseHopSolverSnapshot) {
    const rows = Array.isArray(snapshot?.outputRows) ? snapshot.outputRows : [];
    const meaningfulRows = rows
      .map(caseHopSolverCloneRow)
      .filter(rowHasMeaningfulData);
    return meaningfulRows.filter((row, index) => !(index === 0 && (this.headerRowLooksCanonical?.(row) || caseHopSolverRowLooksLikeRawHeader(row))));
  },

  validateCaseHopSolverRawRows(rows = this.getCaseHopSolverSnapshotDataRows()) {
    return caseHopSolverValidateTool88RawRows(rows);
  },

  stageCaseHopSolverOutputToOutput(options = {}) {
    const rows = this.getCaseHopSolverSnapshotDataRows();
    if (!rows.length) {
      this.caseHopSolverRawApplyStatus = 'Tool 88 has not produced raw-data-style rows yet.';
      return false;
    }
    const outputRows = [
      ['Case Buddy Tool 88 Hop Solver Output', '', '', '', '', '', '', '', '', 'Review these raw-data-style rows before appending to Raw Data or opening graft review.'],
      ...rows
    ];
    this.loadOutputRows(outputRows, {
      title: 'Tool 88 hop solver output',
      detail: options.detail || 'WayFinder-like raw-data rows generated by embedded Tool 88. Main Data was not modified.',
      sourceActionId: options.sourceActionId || 'case_hop_solver_output_staged',
      target: 'raw-data-review'
    });
    this.caseHopSolverRawApplyStatus = `Staged ${rows.length} Tool 88 row${rows.length === 1 ? '' : 's'} in Output/Review.`;
    this.showCaseToast(this.caseHopSolverRawApplyStatus);
    return true;
  },

  appendCaseHopSolverOutputToRawData(options = {}) {
    const rows = this.getCaseHopSolverSnapshotDataRows();
    if (!rows.length) {
      this.caseHopSolverRawApplyStatus = 'Tool 88 has not produced raw-data-style rows to append yet.';
      return { ok: false, rowsAppended: 0, message: this.caseHopSolverRawApplyStatus };
    }
    const validation = caseHopSolverValidateTool88RawRows(rows);
    if (!validation.valid && options.allowIncomplete !== true) {
      this.caseHopSolverRawApplyStatus = `Tool 88 output is not ready to append to Raw Data. ${validation.summary}`;
      return { ok: false, rowsAppended: 0, validation, message: this.caseHopSolverRawApplyStatus };
    }
    if (!options.skipConfirm) {
      const confirmed = window.confirm(`Append ${rows.length} Tool 88 raw-data row${rows.length === 1 ? '' : 's'} into Raw Data? Main Data will not be changed by this append.`);
      if (!confirmed) return { ok: false, rowsAppended: 0, userCancelled: true };
    }
    this.ensureCaseWorkbookScaffold?.({ silent: true });
    const rawSheet = this.findLogicalSheetMeta?.('raw');
    if (!rawSheet?.id) {
      this.caseHopSolverRawApplyStatus = 'Raw Data sheet could not be found.';
      return { ok: false, rowsAppended: 0, message: this.caseHopSolverRawApplyStatus };
    }
    const state = this.getSheetStateById(rawSheet.id);
    const appendResult = buildRawDataAppendMatrix(state.data, rows);
    if (appendResult.rowsAppended <= 0) {
      this.caseHopSolverRawApplyStatus = 'No new Tool 88 rows were appended to Raw Data.';
      return { ok: false, rowsAppended: 0, message: this.caseHopSolverRawApplyStatus };
    }
    this.writeSheetStateById(rawSheet.id, { ...state, data: appendResult.data, frozenTop: 1 });
    this.markCaseDataMutated(options.sourceActionId || 'case_hop_solver_raw_data_appended', {
      rowsAppended: appendResult.rowsAppended,
      startRow: appendResult.startRow,
      candidateRow: this.caseHopSolverCandidate?.spreadsheetRowNumber || '',
      candidateHash: this.caseHopSolverCandidate?.hash || ''
    });
    this.recordWorkflowToolRun?.(options.sourceActionId || 'case_hop_solver_raw_data_appended', {
      rowsAppended: appendResult.rowsAppended,
      candidate: this.caseHopSolverCandidate || null,
      status: this.caseHopSolverSnapshot?.status || ''
    });
    this.recordWorkflowSignal?.('WAYFINDER_RAW_FETCH_SAVED', options.sourceActionId || 'case_hop_solver_raw_data_appended', { silent: true });
    this.caseHopSolverRawApplyStatus = `Appended ${appendResult.rowsAppended} Tool 88 row${appendResult.rowsAppended === 1 ? '' : 's'} to Raw Data.`;
    if (!options.silent) this.showCaseToast(this.caseHopSolverRawApplyStatus);
    this.refreshCaseSummary?.({ light: false });
    this.queueCaseSummaryRefresh?.(120);
    return { ok: true, ...appendResult };
  },

  async openCaseHopSolverGraftReview() {
    const rows = this.getCaseHopSolverSnapshotDataRows();
    const candidate = this.caseHopSolverCandidate;
    if (!candidate?.isPathEnding || !Number.isInteger(candidate.rowIndex)) {
      this.caseHopSolverRawApplyStatus = 'A selected Main Data Path ends row is required before opening graft review.';
      return false;
    }
    if (!rows.length) {
      this.caseHopSolverRawApplyStatus = 'Tool 88 has not produced rows to use for graft review yet.';
      return false;
    }
    await this.openPathOperationModal?.({ rowIndex: candidate.rowIndex, action: 'graft', source: 'tool88-hop-solver' });
    await this.$nextTick?.();
    this.pathOperationRawTsv = buildTsv(rows);
    this.pathOperationSelectedRawSetId = '';
    this.pathOperationConfirmApply = false;
    this.pathOperationStatus = `Tool 88 output loaded for graft review from row ${candidate.spreadsheetRowNumber}. Review the raw rows, then check the confirmation box and apply Graft if it looks correct.`;
    this.caseHopSolverRawApplyStatus = 'Opened graft review with Tool 88 output preloaded.';
    return true;
  },

  async refreshCaseInvestigationVisualizerAfterHopSolver() {
    if (!this.showCaseInvestigationVisualizerModal) return false;
    await this.refreshCaseInvestigationVisualizer?.({ preserveFlow: true, preserveViewport: true });
    this.caseInvestigationVisualizerStatus = 'Investigation visualizer refreshed after Tool 88 update.';
    return true;
  },

  isCaseAutomationInternalHopSolverRawDataEnabled() {
    return this.caseAutomationPreferHopSolverRawData !== false || this.caseAutomationRawDataSourceMode === 'internal-hop-solver';
  },

  async runCaseHopSolverRawFetchUntilComplete(options = {}) {
    if (!this.isCaseAutomationInternalHopSolverRawDataEnabled?.()) {
      return { ok: false, message: 'Internal Tool 88 raw-data solving is disabled.' };
    }
    this.startCaseAutomationTool88Activity?.({
      status: 'starting',
      title: 'Starting Tool 88 raw-data solver',
      message: 'Tool 88 is opening inside Case Buddy and preparing to hydrate the missing raw-data target.'
    });
    await this.openCaseHopSolverModal({ selectNextUnresolved: true, source: 'automation-raw-fetch', autoLoad: false });
    await this.$nextTick?.();
    const tool = this.getCaseHopSolverTool88Ref();
    if (!tool?.caseBuddyLoadRowsForHopSolver) {
      this.finishCaseAutomationTool88Activity?.({ status: 'not-mounted', title: 'Tool 88 did not mount', message: 'Tool 88 is not mounted.' });
      return { ok: false, message: 'Tool 88 is not mounted.' };
    }
    this.caseHopSolverRunning = true;
    this.caseHopSolverAutomationRunning = true;
    this.caseHopSolverStatusTitle = 'Auto raw-data solving';
    this.caseHopSolverStatus = 'Case Buddy Auto is using Tool 88 before falling back to WayFinder.';
    this.updateCaseAutomationTool88ActivityFromSnapshot?.({
      status: 'loading-input',
      message: 'Loading current Main Data into Tool 88 and selecting the first unresolved path ending.'
    }, { eventName: 'case-buddy-progress', active: true });
    try {
      const snapshot = await tool.caseBuddyLoadRowsForHopSolver(this.getCaseHopSolverRowsForTool88(), {
        selectNextUnresolved: true,
        includeRows: true,
        speedMultiplier: this.caseAutomation?.speedMultiplier || 1
      });
      this.handleCaseHopSolverSnapshot(snapshot, { eventName: snapshot?.flowComplete ? 'case-buddy-flow-complete' : 'case-buddy-status' });
      this.updateCaseAutomationTool88ActivityFromSnapshot?.(snapshot, { eventName: 'case-buddy-status', active: true });
      const loopSnapshot = await tool.caseBuddyRunAutomationLoop({
        includeRows: true,
        speedMultiplier: this.caseAutomation?.speedMultiplier || 1,
        ...(options.guardrails || {})
      });
      this.handleCaseHopSolverSnapshot(loopSnapshot, { eventName: loopSnapshot?.flowComplete ? 'case-buddy-flow-complete' : loopSnapshot?.guardrail || loopSnapshot?.needsManualReview ? 'case-buddy-guardrail' : 'case-buddy-result' });
      const rows = this.getCaseHopSolverSnapshotDataRows(loopSnapshot);
      const validation = caseHopSolverValidateTool88RawRows(rows);
      const autoSafeToImport = Boolean(loopSnapshot?.flowComplete && !loopSnapshot?.guardrail && !loopSnapshot?.needsTargetSelection && validation.valid && (!loopSnapshot?.needsManualReview || loopSnapshot?.status === 'flow-complete'));
      if (rows.length > 0 && autoSafeToImport) {
        const append = this.appendCaseHopSolverOutputToRawData({ skipConfirm: true, silent: true, sourceActionId: 'case_hop_solver_auto_raw_fetch' });
        if (append?.ok) {
          this.finishCaseAutomationTool88Activity?.({
            status: 'raw-data-imported',
            title: 'Tool 88 raw data imported',
            message: `Tool 88 solved the raw-data flow and imported ${append.rowsAppended || 0} row${(append.rowsAppended || 0) === 1 ? '' : 's'} into Raw Data.`,
            rowsAppended: append.rowsAppended || 0,
            rawRows: rows.length
          });
          this.addCaseAutomationLog?.(`Tool 88 raw data ready: ${append.rowsAppended || 0} row${(append.rowsAppended || 0) === 1 ? '' : 's'} appended to Raw Data.`);
          this.refreshCaseSummary?.({ light: false });
          if (options.fromAuto !== false) {
            this.resetCaseHopSolverState?.({ close: true, force: true });
            this.showCaseHopSolverModal = false;
          }
          return { ok: true, rowsAppended: append.rowsAppended || 0, snapshot: loopSnapshot, validation };
        }
      }
      const reviewReason = !rows.length
        ? 'Tool 88 did not produce raw rows that could be appended automatically.'
        : !validation.valid
          ? validation.summary
          : loopSnapshot?.needsManualReview
            ? 'Tool 88 paused for manual review before raw data should be imported.'
            : loopSnapshot?.needsTargetSelection
              ? 'Tool 88 needs the user to select a target address before continuing.'
              : loopSnapshot?.guardrail
                ? (loopSnapshot.guardrail.message || 'Tool 88 guardrails paused automation.')
                : 'Tool 88 has not produced a settled flow-complete result yet.';
      this.caseHopSolverRawApplyStatus = reviewReason;
      this.finishCaseAutomationTool88Activity?.({
        status: loopSnapshot?.guardrail ? 'needs-review' : 'not-ready',
        title: loopSnapshot?.guardrail ? 'Tool 88 paused for review' : 'Tool 88 output needs review',
        message: loopSnapshot?.message ? `${loopSnapshot.message} ${reviewReason}` : reviewReason,
        rawRows: rows.length
      });
      return {
        ok: false,
        snapshot: loopSnapshot,
        validation,
        userStopped: loopSnapshot?.guardrail?.reason === 'stop-requested',
        message: loopSnapshot?.message ? `${loopSnapshot.message} ${reviewReason}` : reviewReason
      };
    } finally {
      this.caseHopSolverRunning = false;
      this.caseHopSolverAutomationRunning = false;
    }
  },

  isCaseHopSolverFlowCompleteSettled(snapshot = this.caseHopSolverSnapshot) {
    if (!snapshot?.flowComplete) return false;
    const firstSeen = Number(this.caseHopSolverFlowCompleteFirstSeenAtMs) || 0;
    const confirmations = Number(this.caseHopSolverFlowCompleteConfirmations) || 0;
    return confirmations >= 2 && firstSeen > 0 && Date.now() - firstSeen >= 5000;
  }
};
